import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FiExternalLink } from 'react-icons/fi';
import useToast from 'src/hooks/useToast';
import InfoModal from 'src/components/InfoModal';
import teams from 'src/assets/images/msteams-logo-2.svg';
import slack from 'src/assets/images/slack-logo-2.svg';
import { ReactComponent as Check } from 'src/assets/icons/check-soft.svg';
import { uninstallChatConfiguration } from 'src/redux/modules/chats/chatsActions';
import { ReactComponent as Spinner } from 'src/assets/icons/spinner.svg';
import * as Styled from './style';
import { Wrapper } from '../Connect/style';
import SlackStatusActions from './SlackStatusActions';
import TeamsStatusActions from './TeamsStatusActions';

const platformTypes = {
  SLACK: {
    name: 'Slack',
    logo: <img src={slack} alt="Slack" />,
    canDownload: false,
    center: 'Slack workspace',
    helpDocUrl:
      'https://slack.com/help/articles/360003125231-Remove-apps-and-custom-integrations-from-your-workspace'
  },
  MS_TEAMS: {
    name: 'Microsoft Teams',
    logo: <img src={teams} alt="MsTeams" />,
    canDownload: true,
    center: 'Microsoft Teams admin center',
    helpDocUrl:
      'https://docs.getthread.com/article/yeag82uzj7-how-to-uninstall-the-microsoft-teams-thread-service-app'
  }
};

const Status = ({ uninstallChatConfiguration, chatPlatform, companyId, match }) => {
  const [isFirstConfirmModalOpen, setIsFirstConfirmModalOpen] = useState(false);
  const [isSecondConfirmModalOpen, setIsSecondConfirmModalOpen] = useState(false);
  const [isUninstalling, setIsUninstalling] = useState(false);
  const history = useHistory();
  const { toast } = useToast();
  const platform = chatPlatform?.platform;
  const app = chatPlatform?.app;

  const toggleFirstConfirmModal = () => {
    setIsFirstConfirmModalOpen(!isFirstConfirmModalOpen);
  };

  const toggleSecondConfirmModal = () => {
    setIsSecondConfirmModalOpen(!isSecondConfirmModalOpen);
  };

  const handleFirstConfirmation = () => {
    setIsFirstConfirmModalOpen(false);
    setIsSecondConfirmModalOpen(true);
  };

  const handleUninstallChatConfiguration = () => {
    setIsSecondConfirmModalOpen(false);
    setIsUninstalling(true);

    uninstallChatConfiguration(companyId)
      .then(() => {
        history.push({
          pathname: `/dashboard/clients/companies/${companyId}/connect`,
          state: {
            action: 'updateApp'
          }
        });

        toast.success(`Disconnected successfully`);
      })
      .catch(err => {
        setIsUninstalling(false);
        toast.error('It was not possible to disconnect your app. Please try again later.');

        // eslint-disable-next-line no-console
        console.error('Error occurred while uninstalling chat configuration', err);
      });
  };

  if (!platform) {
    return <Redirect to={{ pathname: `/dashboard/clients/companies/${companyId}/connect` }} />;
  }
  return (
    <Wrapper>
      <Styled.Title>{platformTypes[platform]?.name} Connect</Styled.Title>
      <Styled.Container>
        <Styled.Col>
          <Styled.Subtitle>Platform</Styled.Subtitle>
          <Styled.Wrapper>
            <Styled.Logo>{platformTypes[platform]?.logo}</Styled.Logo>
            <Styled.Description>
              <Styled.Name>{platformTypes[platform]?.name}</Styled.Name>
              <Styled.PlatformID>{chatPlatform?.platform_id}</Styled.PlatformID>
              {chatPlatform?.is_active && (
                <Styled.Status>
                  <Check />
                  Connected
                </Styled.Status>
              )}
            </Styled.Description>
            {chatPlatform?.is_active ? (
              <Styled.DangerOutlineButton
                styleMode="outline"
                size="small"
                onClick={toggleFirstConfirmModal}
              >
                {isUninstalling ? <Spinner width={20} height={20} /> : 'Disconnect'}
              </Styled.DangerOutlineButton>
            ) : (
              <Styled.Status danger>Not connected</Styled.Status>
            )}
          </Styled.Wrapper>
        </Styled.Col>
        {platformTypes[platform] &&
          (platformTypes[platform].canDownload ? (
            <TeamsStatusActions app={app} currentUrl={match.url} companyId={companyId} />
          ) : (
            <SlackStatusActions companyId={companyId} />
          ))}
      </Styled.Container>

      <InfoModal
        isOpen={isFirstConfirmModalOpen}
        toggleHandler={toggleFirstConfirmModal}
        title={`Uninstall ${platformTypes[platform]?.name} in two steps`}
      >
        <Styled.ModalWrapper>
          <ol>
            <li>
              Follow{' '}
              <a href={platformTypes[platform]?.helpDocUrl} target="_blank" rel="noreferrer">
                these steps <FiExternalLink />
              </a>{' '}
              to remove the Thread {platformTypes[platform]?.name} application from your
              customer&apos;s {platformTypes[platform]?.center}.
            </li>
            <li>
              Click &quot;Continue&quot; to disconnect the application from your Thread account to
              complete this process.
            </li>
          </ol>
          <p>
            <strong>Important!</strong> If you are trying to redeploy, you will still need to
            manually remove the Thread {platformTypes[platform]?.name} appplication first.
          </p>
        </Styled.ModalWrapper>
        <Styled.ButtonsWrapper>
          <Styled.CancelButton onClick={toggleFirstConfirmModal}>Cancel</Styled.CancelButton>
          <Styled.ConfirmButton onClick={handleFirstConfirmation}>Continue</Styled.ConfirmButton>
        </Styled.ButtonsWrapper>
      </InfoModal>

      <InfoModal
        isOpen={isSecondConfirmModalOpen}
        toggleHandler={toggleSecondConfirmModal}
        title={`Disconnect ${platformTypes[platform]?.name}`}
      >
        <Styled.ModalWrapper>
          <p>
            Confirming this action will disconnect your custom {platformTypes[platform]?.name} app
            from your customer&apos;s tenant.
          </p>
        </Styled.ModalWrapper>
        <Styled.ButtonsWrapper>
          <Styled.CancelButton onClick={toggleSecondConfirmModal}>Cancel</Styled.CancelButton>
          <Styled.ConfirmButton styleMode="danger" onClick={handleUninstallChatConfiguration}>
            Yes, disconnect
          </Styled.ConfirmButton>
        </Styled.ButtonsWrapper>
      </InfoModal>
    </Wrapper>
  );
};

Status.propTypes = {
  companyId: PropTypes.number.isRequired,
  match: PropTypes.shape({
    url: PropTypes.string
  }).isRequired,
  chatPlatform: PropTypes.shape({
    platform: PropTypes.string,
    platform_id: PropTypes.string,
    is_active: PropTypes.bool,
    app: PropTypes.object
  }),
  uninstallChatConfiguration: PropTypes.func.isRequired
};

Status.defaultProps = {
  chatPlatform: null
};

const mapStateToProps = state => ({
  chatConfiguration: state.usersReducer.user.chatConfiguration
});

const mapDispatchToProps = dispatch => ({
  uninstallChatConfiguration: companyId => dispatch(uninstallChatConfiguration(companyId))
});

export default connect(mapStateToProps, mapDispatchToProps)(Status);
